/**
 * Sign-up, sign-in, verification and password recovery.
 *
 * One implementation for both portals, differing only in audience labelling.
 * Copy follows the server: where the API deliberately gives a neutral answer
 * - "if that address can be used" - the screen says the same thing rather
 * than dressing it up as success, because a confident "check your inbox" on
 * an address that is already registered is the oracle the API avoids.
 */
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Button, Card, Eyebrow, LinkButton } from "@ui/primitives";
import { TextInput, Checkbox, FormError, FormNotice } from "@ui/forms";
import { fontSize, radius } from "@design/index";
import { api, type ApiFieldErrors } from "./api";
import { publicSiteUrl } from "./origins";

export type AuthAudience = "creator" | "brand";

const AUDIENCE_COPY: Record<AuthAudience, { noun: string; blurb: string }> = {
  creator: {
    noun: "creator",
    blurb: "Find funded campaigns, post on your own accounts and track what you earn.",
  },
  brand: {
    noun: "brand",
    blurb: "Brief a campaign, fund it up front and review the work that comes in.",
  },
};

function AuthLayout({ title, subtitle, children }: {
  title: string; subtitle?: string; children: ReactNode;
}) {
  return (
    <div style={{ minHeight: "100vh", background: "var(--canvas)", color: "var(--text)",
                  display: "grid", placeItems: "center", padding: "40px 16px" }}>
      <div style={{ width: "100%", maxWidth: 440, display: "grid", gap: 20 }}>
        <a href={publicSiteUrl("#/")} style={{ display: "inline-flex", alignItems: "center", gap: 10,
                             color: "var(--text)", textDecoration: "none", justifySelf: "center" }}>
          <svg viewBox="0 0 32 32" width="26" height="26" aria-hidden="true" style={{ color: "var(--accent)" }}>
            <rect x="2" y="7.6" width="15" height="4.8" rx="2.4" fill="currentColor" />
            <rect x="2" y="13.6" width="23" height="4.8" rx="2.4" fill="currentColor" />
            <rect x="2" y="19.6" width="11" height="4.8" rx="2.4" fill="currentColor" />
            <rect x="19.9" y="4" width="2.2" height="26" rx="1.1" fill="currentColor" />
            <path d="M17.4 2.6h7.2l-1.9 2.6h-3.4z" fill="currentColor" />
          </svg>
          <span style={{ fontWeight: 700, fontSize: fontSize.h4 }}>
            ClipMaster<span style={{ color: "var(--accent)" }}>.io</span>
          </span>
        </a>
        <Card padding={28} style={{ display: "grid", gap: 18 }}>
          <div style={{ display: "grid", gap: 6 }}>
            <h1 style={{ fontSize: fontSize.h2, fontWeight: 700, letterSpacing: "-0.01em" }}>{title}</h1>
            {subtitle ? (
              <p style={{ fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.55 }}>{subtitle}</p>
            ) : null}
          </div>
          {children}
        </Card>
        <a href={publicSiteUrl("#/")}
          style={{ justifySelf: "center", fontSize: fontSize.body, color: "var(--muted)" }}>
          Back to clipmaster.io
        </a>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- sign up */

export function SignUpScreen({ audience, onNavigate }: {
  audience: AuthAudience; onNavigate: (to: string) => void;
}) {
  const copy = AUDIENCE_COPY[audience];
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [fields, setFields] = useState<ApiFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setFields({}); setFormError(null);
    const res = await api.post<{ message: string }>("/auth/signup", {
      email, password, displayName, acceptedTerms: accepted,
    });
    setBusy(false);
    if (res.ok) { setDone(true); return; }
    if (res.fields) setFields(res.fields);
    else setFormError(res.message);
  }

  if (done) {
    return (
      <AuthLayout title="Check your inbox">
        {/* Deliberately not "we sent you an email": the server answers the
            same way whether or not the address was already registered. */}
        <FormNotice message="If that address can be used, we have sent an email with the next step." />
        <p style={{ fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6 }}>
          The link is valid for 24 hours. If nothing arrives, check your spam folder, then try
          signing in - the address may already have an account.
        </p>
        <LinkButton href="/login" variant="secondary">Go to sign in</LinkButton>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={`Create your ${copy.noun} account`} subtitle={copy.blurb}>
      <SocialSignIn audience={audience} termsNote />
      <form onSubmit={submit} style={{ display: "grid", gap: 16 }} noValidate>
        {formError ? <FormError message={formError} /> : null}
        <TextInput
          label={audience === "brand" ? "Your name" : "Display name"} required
          autoComplete="name" value={displayName} error={fields["displayName"]}
          onChange={(e) => setDisplayName(e.currentTarget.value)}
          hint={audience === "creator" ? "Shown publicly. Your legal name for payouts is separate." : undefined}
        />
        <TextInput
          label="Email" type="email" required autoComplete="email" value={email}
          error={fields["email"]} onChange={(e) => setEmail(e.currentTarget.value)}
        />
        <TextInput
          label="Password" type="password" required autoComplete="new-password"
          value={password} error={fields["password"]}
          onChange={(e) => setPassword(e.currentTarget.value)}
          hint="At least 12 characters. A memorable phrase beats a short complicated one."
        />
        <Checkbox
          checked={accepted} onChange={setAccepted} error={fields["acceptedTerms"]}
          label={
            <>
              I accept the <a href={publicSiteUrl("/legal/terms")}>terms of service</a> and the{" "}
              <a href={publicSiteUrl("/legal/privacy")}>privacy notice</a>.
            </>
          }
        />
        <Button type="submit" variant="primary" size="lg" block disabled={busy}>
          {busy ? "Creating account…" : "Create account"}
        </Button>
      </form>
      <p style={{ fontSize: fontSize.body, color: "var(--muted)", textAlign: "center" }}>
        Already have an account?{" "}
        <a href="/login" onClick={(e) => { e.preventDefault(); onNavigate("/login"); }}>Sign in</a>
      </p>
    </AuthLayout>
  );
}

/* ---------------------------------------------------------- social SSO */

interface SsoProvider { id: string; label: string; via: "firebase" | "workos" }

/** Flaticon brand marks, served from each portal's /icons. */
const SSO_ICONS: Record<string, string> = {
  GoogleOAuth: "/icons/sso-google.png",
  MicrosoftOAuth: "/icons/sso-microsoft.png",
};

const SSO_ERRORS: Record<string, string> = {
  failed: "Could not sign you in with that account. Try again or use email.",
  cancelled: "Sign-in was cancelled.",
  unverified: "That sign-in provider has not verified the email address.",
};

function ssoErrorFromLocation(): string | null {
  if (typeof window === "undefined") return null;
  const reason = new URLSearchParams(window.location.search).get("sso");
  return reason && SSO_ERRORS[reason] ? SSO_ERRORS[reason] : null;
}

function ssoStart(p: SsoProvider): string {
  return p.via === "firebase"
    ? "/api/v1/auth/firebase/start"
    : `/api/v1/auth/workos/start?provider=${encodeURIComponent(p.id)}`;
}

/**
 * Social buttons. Google is Firebase Identity Toolkit; Microsoft is WorkOS.
 * Either request 404s when that provider is unbound, same as the demo panel.
 *
 * Navigation is a full page load, not the SPA router: the start URLs 302
 * off-origin, and the in-app click interceptor would swallow an ordinary
 * same-origin `<a>`.
 */
function SocialSignIn({ audience, termsNote }: {
  audience: AuthAudience; termsNote?: boolean;
}) {
  const [providers, setProviders] = useState<SsoProvider[]>([]);

  useEffect(() => {
    if (audience !== "creator" && audience !== "brand") return;
    let live = true;
    void api.get<{ providers: SsoProvider[] }>("/auth/sso/providers").then((r) => {
      if (live && r.ok && r.data.providers.length > 0) setProviders(r.data.providers);
    });
    return () => { live = false; };
  }, [audience]);

  if (providers.length === 0) return null;

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {providers.map((p) => (
        <Button
          key={p.id}
          type="button"
          variant="secondary"
          size="lg"
          block
          onClick={() => {
            window.location.assign(ssoStart(p));
          }}
        >
          {SSO_ICONS[p.id] ? (
            <img
              src={SSO_ICONS[p.id]}
              alt=""
              width={20}
              height={20}
              style={{ flexShrink: 0, display: "block" }}
            />
          ) : null}
          Continue with {p.label}
        </Button>
      ))}
      {termsNote ? (
        <p style={{ margin: 0, fontSize: fontSize.micro, color: "var(--faint)",
                    lineHeight: 1.55, textAlign: "center" }}>
          By continuing you accept the{" "}
          <a href={publicSiteUrl("/legal/terms")}>terms of service</a> and the{" "}
          <a href={publicSiteUrl("/legal/privacy")}>privacy notice</a>.
        </p>
      ) : null}
      <div style={{ display: "flex", alignItems: "center", gap: 10,
                    color: "var(--faint)", fontSize: fontSize.micro }}>
        <span style={{ flex: 1, height: 1, background: "var(--line)" }} />
        or continue with email
        <span style={{ flex: 1, height: 1, background: "var(--line)" }} />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- log in */

interface DemoAccount { email: string; label: string; role: string }
interface DemoLogins { environment: string; password: string; accounts: DemoAccount[] }

/**
 * The demo-account panel. UAT only, and empty everywhere else.
 *
 * Nothing about the accounts is in this file or anywhere else in the
 * bundle - no address, no password, not even the list of roles. The panel
 * asks the server, and the server answers only where `SHOW_DEMO_LOGINS`
 * and `DEMO_LOGIN_PASSWORD` are both set, which is UAT. In production the
 * request is a 404, `accounts` stays empty, and this component renders
 * `null`: the sign-in page is the same page it was before.
 *
 * Clicking a row fills the form and stops. Signing in is still the
 * person's click on the real button, through the real endpoint - a panel
 * that logged you in would be a second way into the product, and the one
 * being tested here is the first one.
 */
function DemoLoginPanel({ onPick }: { onPick: (email: string, password: string) => void }) {
  const [demo, setDemo] = useState<DemoLogins | null>(null);

  useEffect(() => {
    let live = true;
    void api.get<DemoLogins>("/auth/demo-logins").then((r) => {
      // A 404 is the expected answer in production, so it is not an error
      // and nothing is reported: there is simply no panel.
      if (live && r.ok && r.data.accounts.length > 0) setDemo(r.data);
    });
    return () => { live = false; };
  }, []);

  if (!demo) return null;

  return (
    <Card padding={14} style={{ display: "grid", gap: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <Eyebrow>Demo users</Eyebrow>
        <span style={{ fontSize: fontSize.micro, color: "var(--warn-fg)",
                       textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {demo.environment.toUpperCase()} - demo accounts only
        </span>
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        {demo.accounts.map((a) => (
          <button
            key={a.email}
            type="button"
            onClick={() => onPick(a.email, demo.password)}
            style={{
              display: "grid", gap: 2, textAlign: "left", cursor: "pointer",
              padding: "8px 10px", borderRadius: radius.sm, font: "inherit",
              background: "var(--surface-2)", border: "1px solid var(--line)",
              color: "var(--text)",
            }}
          >
            <span style={{ fontSize: fontSize.label, fontWeight: 600 }}>{a.label}</span>
            <span style={{ fontSize: fontSize.micro, color: "var(--muted)" }}>
              {a.role} · {a.email}
            </span>
          </button>
        ))}
      </div>

      <p style={{ margin: 0, fontSize: fontSize.micro, color: "var(--faint)",
                  lineHeight: 1.6 }}>
        Picking one fills the form below. You still press Sign in.
      </p>
    </Card>
  );
}

export function LoginScreen({ audience, onSignedIn, onNavigate }: {
  audience: AuthAudience; onSignedIn: () => void; onNavigate: (to: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(ssoErrorFromLocation);
  const [busy, setBusy] = useState(false);
  const [firebaseReturn, setFirebaseReturn] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash.startsWith("#")
      ? new URLSearchParams(window.location.hash.slice(1))
      : new URLSearchParams();
    const idToken = hash.get("id_token");
    const state = hash.get("state");
    const accessToken = hash.get("access_token");
    const error = hash.get("error");
    if (!idToken && !error) return;
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    if (error === "access_denied") {
      setFormError(SSO_ERRORS.cancelled);
      return;
    }
    if (!idToken || !state) {
      setFormError(SSO_ERRORS.failed);
      return;
    }
    setFirebaseReturn(true);
    void api.post<{ ok: boolean }>("/auth/firebase/google", {
      idToken, state, ...(accessToken ? { accessToken } : {}),
    }).then((r) => {
      if (r.ok) { onSignedIn(); return; }
      setFirebaseReturn(false);
      setFormError(r.message || SSO_ERRORS.failed);
    });
  }, [onSignedIn]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setFormError(null);
    const res = await api.post<{ emailVerified: boolean }>("/auth/login", { email, password });
    setBusy(false);
    if (res.ok) { onSignedIn(); return; }
    // One message for every failure mode. Which of "no such account",
    // "wrong password" and "suspended" it was is not the guesser's business.
    setFormError(res.message);
  }

  if (firebaseReturn) {
    return (
      <AuthLayout title="Sign in" subtitle={`${AUDIENCE_COPY[audience].noun} portal`}>
        <p style={{ fontSize: fontSize.body, color: "var(--muted)" }}>Signing you in with Google…</p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Sign in" subtitle={`${AUDIENCE_COPY[audience].noun} portal`}>
      <DemoLoginPanel onPick={(e, p) => { setEmail(e); setPassword(p); setFormError(null); }} />
      <SocialSignIn audience={audience} />
      <form onSubmit={submit} style={{ display: "grid", gap: 16 }} noValidate>
        {formError ? <FormError message={formError} /> : null}
        <TextInput label="Email" type="email" required autoComplete="email"
                   value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
        <TextInput label="Password" type="password" required autoComplete="current-password"
                   value={password} onChange={(e) => setPassword(e.currentTarget.value)} />
        <Button type="submit" variant="primary" size="lg" block disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </Button>
      </form>
      <div style={{ display: "grid", gap: 8, textAlign: "center", fontSize: fontSize.body,
                    color: "var(--muted)" }}>
        <a href="/forgot-password"
           onClick={(e) => { e.preventDefault(); onNavigate("/forgot-password"); }}>
          Forgot your password?
        </a>
        <span>
          New here?{" "}
          <a href="/signup" onClick={(e) => { e.preventDefault(); onNavigate("/signup"); }}>
            Create an account
          </a>
        </span>
      </div>
    </AuthLayout>
  );
}

/* -------------------------------------------------------- verify email */

export function VerifyEmailScreen({ token, onDone }: { token: string | null; onDone: () => void }) {
  const [state, setState] = useState<"idle" | "busy" | "ok" | "error">("idle");
  const [message, setMessage] = useState("");

  async function verify() {
    if (!token) { setState("error"); setMessage("That link is missing its token."); return; }
    setState("busy");
    const res = await api.post<{ verified: boolean }>("/auth/verify-email", { token });
    if (res.ok) { setState("ok"); return; }
    setState("error"); setMessage(res.message);
  }

  if (state === "idle" && token) void verify();

  return (
    <AuthLayout title="Verify your email">
      {state === "busy" || state === "idle" ? (
        <p style={{ fontSize: fontSize.body, color: "var(--muted)" }}>Checking your link…</p>
      ) : null}
      {state === "ok" ? (
        <>
          <FormNotice message="Your email address is verified." />
          <Button variant="primary" size="lg" block onClick={onDone}>Continue</Button>
        </>
      ) : null}
      {state === "error" ? (
        <>
          <FormError message={message} />
          <p style={{ fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6 }}>
            Verification links are single-use and expire after 24 hours. Sign in and request a
            new one.
          </p>
          <LinkButton href="/login" variant="secondary">Go to sign in</LinkButton>
        </>
      ) : null}
    </AuthLayout>
  );
}

/* ---------------------------------------------------- password recovery */

export function ForgotPasswordScreen({ onNavigate }: { onNavigate: (to: string) => void }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    await api.post("/auth/password/forgot", { email });
    setBusy(false);
    setDone(true);   // always, whatever the server said
  }

  if (done) {
    return (
      <AuthLayout title="Check your inbox">
        <FormNotice message="If that address can be used, we have sent an email with the next step." />
        <p style={{ fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6 }}>
          The reset link is valid for one hour and can be used once.
        </p>
        <LinkButton href="/login" variant="secondary">Back to sign in</LinkButton>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset your password"
                subtitle="Enter your email address and we will send you a single-use link.">
      <form onSubmit={submit} style={{ display: "grid", gap: 16 }} noValidate>
        <TextInput label="Email" type="email" required autoComplete="email"
                   value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
        <Button type="submit" variant="primary" size="lg" block disabled={busy}>
          {busy ? "Sending…" : "Send reset link"}
        </Button>
      </form>
      <p style={{ fontSize: fontSize.body, color: "var(--muted)", textAlign: "center" }}>
        <a href="/login" onClick={(e) => { e.preventDefault(); onNavigate("/login"); }}>
          Back to sign in
        </a>
      </p>
    </AuthLayout>
  );
}

export function ResetPasswordScreen({ token, onNavigate }: {
  token: string | null; onNavigate: (to: string) => void;
}) {
  const [password, setPassword] = useState("");
  const [fields, setFields] = useState<ApiFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setFields({}); setFormError(null);
    const res = await api.post("/auth/password/reset", { token, password });
    setBusy(false);
    if (res.ok) { setDone(true); return; }
    if (res.fields) setFields(res.fields);
    else setFormError(res.message);
  }

  if (done) {
    return (
      <AuthLayout title="Password updated">
        <FormNotice message="Sign in with your new password." />
        {/* The server revoked every other session in the same transaction. */}
        <p style={{ fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6 }}>
          Every other signed-in device has been signed out.
        </p>
        <Button variant="primary" size="lg" block onClick={() => onNavigate("/login")}>
          Go to sign in
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password">
      <form onSubmit={submit} style={{ display: "grid", gap: 16 }} noValidate>
        {formError ? <FormError message={formError} /> : null}
        <TextInput label="New password" type="password" required autoComplete="new-password"
                   value={password} error={fields["password"]}
                   onChange={(e) => setPassword(e.currentTarget.value)}
                   hint="At least 12 characters." />
        <Button type="submit" variant="primary" size="lg" block disabled={busy}>
          {busy ? "Updating…" : "Update password"}
        </Button>
      </form>
    </AuthLayout>
  );
}

/* --------------------------------------------------- verification nudge */

/** Shown inside the portal while the address is unverified. */
export function VerifyBanner({ email }: { email: string }) {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function resend() {
    setBusy(true);
    await api.post("/auth/resend-verification");
    setBusy(false); setSent(true);
  }

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center",
                  justifyContent: "space-between", padding: "12px 16px",
                  background: "var(--tone-warn-bg)", color: "var(--tone-warn-fg)",
                  borderRadius: radius.sm }}>
      <div style={{ display: "grid", gap: 2 }}>
        <Eyebrow>Verify your email</Eyebrow>
        <span style={{ fontSize: fontSize.body }}>
          {sent
            ? "Sent. Check your inbox for the new link."
            : `Confirm ${email} to finish setting up your account.`}
        </span>
      </div>
      {!sent ? (
        <Button size="sm" onClick={resend} disabled={busy}>
          {busy ? "Sending…" : "Resend link"}
        </Button>
      ) : null}
    </div>
  );
}
